/**
 * Reusable Mantine modal for creating or editing a meter through the
 * /v1/admin/meters endpoints (§9.4 of DEVELOPER_CONTINUATION_GUIDE).
 *
 * Why one component for both flows:
 *   - The shape we mutate is almost identical (display_name, kind, unit,
 *     status), and the create flow only adds two more required fields
 *     (site_slug, slug) that we render conditionally.
 *   - It keeps the validation + error-display story in a single place,
 *     so the 422 from a bad slug looks the same whether the operator hit
 *     "Create" on the admin page or "Edit" inline on a device row.
 *
 * Slug shape and status enum match the API schema verbatim. Anything else
 * (kind, unit) is free-form on the server, so we render Autocomplete with
 * the suggested vocabulary but never hard-block input.
 */
import {
  Alert,
  Autocomplete,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  TextInput,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useEffect, useState, type FormEvent } from "react";

import { ApiError } from "@/api/client";
import { useCreateMeter, useUpdateMeter } from "@/api/queries";
import {
  METER_KIND_SUGGESTIONS,
  METER_STATUSES,
  METER_UNIT_SUGGESTIONS,
  type AdminMeter,
  type AdminMeterStatus,
} from "@/api/types";
import { SiteSelect } from "@/components/SiteSelect";

type CreateDefaults = {
  site_slug?: string;
  device_slug?: string | null;
};

export type MeterFormModalProps = {
  opened: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  meter?: AdminMeter;
  /** Pre-fills site_slug / device_slug in create mode. */
  defaults?: CreateDefaults;
  onSuccess?: (meter: AdminMeter) => void;
};

type FormValues = {
  site_slug: string;
  device_slug: string;
  slug: string;
  display_name: string;
  kind: string;
  unit: string;
  status: AdminMeterStatus;
};

type FieldErrors = Partial<Record<keyof FormValues, string>>;

const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9_-]*[a-z0-9])?$/u;
const SLUG_HINT = "lowercase slug, [a-z0-9_-], must start and end with alphanumeric";

function defaultValues(
  mode: MeterFormModalProps["mode"],
  meter: AdminMeter | undefined,
  defaults: CreateDefaults | undefined,
): FormValues {
  if (mode === "edit" && meter) {
    return {
      site_slug: meter.site_slug,
      device_slug: meter.device_slug ?? "",
      slug: meter.slug,
      display_name: meter.display_name,
      kind: meter.kind,
      unit: meter.unit,
      status: (METER_STATUSES as readonly string[]).includes(meter.status)
        ? (meter.status as AdminMeterStatus)
        : "active",
    };
  }
  return {
    site_slug: defaults?.site_slug ?? "",
    device_slug: defaults?.device_slug ?? "",
    slug: "",
    display_name: "",
    kind: "",
    unit: "",
    status: "active",
  };
}

function validate(values: FormValues, mode: "create" | "edit"): FieldErrors {
  const errors: FieldErrors = {};
  if (mode === "create") {
    // site_slug comes from SiteSelect, so it's either a valid slug or "".
    // Distinguish "you forgot to pick" from "your slug is malformed" so
    // the inline error makes sense for each surface.
    if (values.site_slug.length === 0) {
      errors.site_slug = "required";
    } else if (!SLUG_PATTERN.test(values.site_slug)) {
      errors.site_slug = SLUG_HINT;
    }
    if (!SLUG_PATTERN.test(values.slug)) errors.slug = SLUG_HINT;
    if (values.device_slug !== "" && !SLUG_PATTERN.test(values.device_slug)) {
      errors.device_slug = SLUG_HINT;
    }
  }
  if (values.display_name.trim().length === 0) errors.display_name = "required";
  if (values.kind.trim().length === 0) errors.kind = "required";
  if (values.unit.trim().length === 0) errors.unit = "required";
  return errors;
}

export function MeterFormModal({
  opened,
  onClose,
  mode,
  meter,
  defaults,
  onSuccess,
}: MeterFormModalProps) {
  const createMutation = useCreateMeter();
  const updateMutation = useUpdateMeter();
  const mutation = mode === "create" ? createMutation : updateMutation;

  const [values, setValues] = useState<FormValues>(() => defaultValues(mode, meter, defaults));
  const [errors, setErrors] = useState<FieldErrors>({});

  // Reset whenever the modal is reopened against a different target.
  // Without this, opening Edit on meter A then meter B would keep A's
  // values until the user clicked into a field.
  useEffect(() => {
    if (opened) {
      setValues(defaultValues(mode, meter, defaults));
      setErrors({});
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, mode, meter?.id]);

  const set = <K extends keyof FormValues>(field: K, value: FormValues[K]) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const fieldErrors = validate(values, mode);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    try {
      if (mode === "create") {
        const created = await createMutation.mutateAsync({
          site_slug: values.site_slug,
          device_slug: values.device_slug.trim() === "" ? null : values.device_slug,
          slug: values.slug,
          display_name: values.display_name,
          kind: values.kind,
          unit: values.unit,
          status: values.status,
        });
        onSuccess?.(created);
      } else if (meter) {
        const updated = await updateMutation.mutateAsync({
          id: meter.id,
          body: {
            display_name: values.display_name,
            kind: values.kind,
            unit: values.unit,
            status: values.status,
          },
        });
        onSuccess?.(updated);
      }
      onClose();
    } catch {
      // Error is surfaced via `mutation.error` and rendered inline below.
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={mode === "create" ? "Create meter" : `Edit ${meter?.display_name ?? "meter"}`}
      centered
      size="md"
    >
      <form onSubmit={handleSubmit} noValidate>
        <Stack>
          {mutation.error && <FormErrorAlert error={mutation.error} />}

          {mode === "create" && (
            <>
              <SiteSelect
                label="Site"
                description="Site that owns this meter (immutable)."
                required
                value={values.site_slug || null}
                onChange={(v) => set("site_slug", v ?? "")}
                error={errors.site_slug}
              />
              <TextInput
                label="Device slug"
                description="Optional. Leave blank for an ungrouped meter."
                value={values.device_slug}
                onChange={(e) => set("device_slug", e.currentTarget.value)}
                error={errors.device_slug}
              />
              <TextInput
                label="Meter slug"
                description="Lowercase identifier, unique within the site."
                required
                value={values.slug}
                onChange={(e) => set("slug", e.currentTarget.value)}
                error={errors.slug}
              />
            </>
          )}

          {mode === "edit" && meter && (
            <TextInput
              label="Slug"
              description="Slug and site are immutable — re-create the meter to change them."
              value={`${meter.site_slug} / ${meter.device_slug ?? "(ungrouped)"} / ${meter.slug}`}
              readOnly
              variant="filled"
            />
          )}

          <TextInput
            label="Display name"
            required
            value={values.display_name}
            onChange={(e) => set("display_name", e.currentTarget.value)}
            error={errors.display_name}
          />

          <Autocomplete
            label="Kind"
            required
            data={[...METER_KIND_SUGGESTIONS]}
            description="Suggested values; any free-form string is accepted."
            value={values.kind}
            onChange={(value) => set("kind", value)}
            error={errors.kind}
          />

          <Autocomplete
            label="Unit"
            required
            data={[...METER_UNIT_SUGGESTIONS]}
            description="Suggested values; any free-form string is accepted."
            value={values.unit}
            onChange={(value) => set("unit", value)}
            error={errors.unit}
          />

          <Select
            label="Status"
            data={METER_STATUSES.map((s) => ({ value: s, label: s }))}
            allowDeselect={false}
            value={values.status}
            onChange={(value) => {
              if (value && (METER_STATUSES as readonly string[]).includes(value)) {
                set("status", value as AdminMeterStatus);
              }
            }}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {mode === "create" ? "Create" : "Save changes"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

/**
 * Friendly inline error renderer. We treat `meter_conflict` (slug clash)
 * separately because it has actionable guidance; everything else falls
 * back to the standard `code: message` shape.
 */
function FormErrorAlert({ error }: { error: unknown }) {
  if (error instanceof ApiError && error.code === "meter_conflict") {
    return (
      <Alert
        icon={<IconAlertCircle size={18} />}
        color="orange"
        variant="light"
        title="Slug already in use"
      >
        A meter with that slug already exists in this site. Pick a different
        slug or edit the existing one.
      </Alert>
    );
  }
  const message =
    error instanceof ApiError
      ? `${error.code}: ${error.message}`
      : error instanceof Error
        ? error.message
        : "Unknown error";
  return (
    <Alert icon={<IconAlertCircle size={18} />} color="red" variant="light" title="Save failed">
      {message}
    </Alert>
  );
}
